import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Upload, X, Loader2, CheckCircle2, FileText, Phone, Mail, MapPin } from "lucide-react";
import { TID } from "@/constants/testIds";
import { API_BASE } from "@/lib/api";
import { SERVICES } from "@/components/site/Solutions";

const INDUSTRIES = ["Manufacturing", "Healthcare", "Pharmaceuticals", "Retail", "Distribution", "Warehousing", "Transportation", "Logistics", "Government", "Financial Services", "Professional Services", "Real Estate", "Other"];
const EMPLOYEE_RANGES = ["1-10", "11-50", "51-200", "201-500", "501-1000", "1000+"];
const PRIORITIES = ["Critical", "High", "Medium", "Low"];
const ALLOWED = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".jpg", ".jpeg", ".png"];
const MAX_SIZE = 25 * 1024 * 1024;

export default function ContactForm({ formRef }) {
  const [data, setData] = useState({
    full_name: "", company_name: "", job_title: "", phone_number: "", email: "",
    services: [], industry: "", employees: "", location: "", priority: "Medium",
    description: "", preferred_contact_method: "Email", preferred_contact_time: "Morning",
    consent: false,
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleService = (s) =>
    setData((d) => ({ ...d, services: d.services.includes(s) ? d.services.filter((x) => x !== s) : [...d.services, s] }));

  const addFiles = (list) => {
    const incoming = Array.from(list);
    const valid = [];
    for (const f of incoming) {
      const ext = "." + f.name.split(".").pop().toLowerCase();
      if (!ALLOWED.includes(ext)) { toast.error(`${f.name}: unsupported type`); continue; }
      if (f.size > MAX_SIZE) { toast.error(`${f.name}: exceeds 25 MB`); continue; }
      valid.push(f);
    }
    setFiles((prev) => [...prev, ...valid]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!data.consent) return toast.error("Please confirm your consent");
    if (!data.full_name || !data.phone_number || !data.email || !data.description) {
      return toast.error("Please fill in name, phone, email, and description");
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify(data));
      files.forEach((f) => fd.append("files", f));
      const res = await fetch(`${API_BASE}/inquiries`, { method: "POST", body: fd });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Submission failed");
      }
      setDone(true);
      toast.success("Thank you. We'll reach out within 24 hours.");
    } catch (err) {
      toast.error(err.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section id="contact" ref={formRef} className="bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-24 text-center">
          <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5 }}>
            <CheckCircle2 size={72} className="mx-auto text-[#10B981] mb-6" />
          </motion.div>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl text-[#0A2540] tracking-tight">Thank you for contacting Clinivon.</h2>
          <p className="mt-4 text-slate-600">Our consultants will review your inquiry and contact you shortly.</p>
          <p className="mt-2 text-slate-600">For urgent assistance call:</p>
          <a href="tel:9429951975" className="mt-2 inline-flex items-center gap-2 font-display font-bold text-2xl text-[#0A2540]">
            <Phone size={20} /> 942-995-1975
          </a>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" ref={formRef} className="bg-[#F8FAFC] border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Left intro */}
          <div className="lg:col-span-4">
            <div className="section-eyebrow mb-5">Tell Us Your Challenge</div>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0A2540] leading-tight">
              A consultant will contact you within 24 hours.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Whether you need process improvement, contract review, SOP development, KPI creation, change
              management support, or operational optimization — our experts are ready to help.
            </p>
            <div className="mt-10 space-y-4 border-t border-slate-200 pt-6">
              <div className="flex items-center gap-3 text-sm text-slate-700"><Phone size={16} className="text-[#10B981]" /> 942-995-1975</div>
              <div className="flex items-center gap-3 text-sm text-slate-700"><Mail size={16} className="text-[#10B981]" /> cs@clinivon.com</div>
              <div className="flex items-center gap-3 text-sm text-slate-700"><MapPin size={16} className="text-[#10B981]" /> Confidential. Secure. Fast turnaround.</div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-8 bg-white border border-slate-200 p-6 md:p-10 space-y-8">
            {/* Personal */}
            <div>
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-4">Personal Information</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full Name *">
                  <input data-testid={TID.form.fullName} required value={data.full_name} onChange={(e) => update("full_name", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Company Name">
                  <input data-testid={TID.form.companyName} value={data.company_name} onChange={(e) => update("company_name", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Job Title">
                  <input data-testid={TID.form.jobTitle} value={data.job_title} onChange={(e) => update("job_title", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Phone Number *">
                  <input data-testid={TID.form.phone} required value={data.phone_number} onChange={(e) => update("phone_number", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Email Address *" className="sm:col-span-2">
                  <input data-testid={TID.form.email} required type="email" value={data.email} onChange={(e) => update("email", e.target.value)} className={inputCls} />
                </Field>
              </div>
            </div>

            {/* Services */}
            <div>
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-4">Services Required</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {SERVICES.map((s) => {
                  const active = data.services.includes(s.t);
                  return (
                    <label key={s.id} className={`flex items-center gap-2 px-3 py-2.5 border cursor-pointer text-sm transition-colors ${active ? "bg-[#0A2540] text-white border-[#0A2540]" : "bg-white border-slate-200 hover:border-[#0A2540]"}`}>
                      <input
                        data-testid={TID.form.serviceCheckbox(s.t)}
                        type="checkbox"
                        checked={active}
                        onChange={() => toggleService(s.t)}
                        className="sr-only"
                      />
                      <span className={`h-4 w-4 grid place-items-center border ${active ? "bg-[#10B981] border-[#10B981]" : "border-slate-300"}`}>
                        {active && <span className="h-2 w-2 bg-white" />}
                      </span>
                      {s.t}
                    </label>
                  );
                })}
                <label className={`flex items-center gap-2 px-3 py-2.5 border cursor-pointer text-sm transition-colors ${data.services.includes("Other") ? "bg-[#0A2540] text-white border-[#0A2540]" : "bg-white border-slate-200 hover:border-[#0A2540]"}`}>
                  <input type="checkbox" checked={data.services.includes("Other")} onChange={() => toggleService("Other")} className="sr-only" />
                  <span className={`h-4 w-4 grid place-items-center border ${data.services.includes("Other") ? "bg-[#10B981] border-[#10B981]" : "border-slate-300"}`}>
                    {data.services.includes("Other") && <span className="h-2 w-2 bg-white" />}
                  </span>
                  Other
                </label>
              </div>
            </div>

            {/* Business */}
            <div>
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-4">Business Information</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Industry">
                  <select data-testid={TID.form.industry} value={data.industry} onChange={(e) => update("industry", e.target.value)} className={inputCls}>
                    <option value="">Select…</option>
                    {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
                  </select>
                </Field>
                <Field label="Number of Employees">
                  <select data-testid={TID.form.employees} value={data.employees} onChange={(e) => update("employees", e.target.value)} className={inputCls}>
                    <option value="">Select…</option>
                    {EMPLOYEE_RANGES.map((r) => <option key={r}>{r}</option>)}
                  </select>
                </Field>
                <Field label="Business Location">
                  <input data-testid={TID.form.location} value={data.location} onChange={(e) => update("location", e.target.value)} className={inputCls} placeholder="City, Country" />
                </Field>
                <Field label="Priority Level">
                  <select data-testid={TID.form.priority} value={data.priority} onChange={(e) => update("priority", e.target.value)} className={inputCls}>
                    {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </Field>
              </div>
            </div>

            {/* Description */}
            <div>
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-4">Requirement Description</div>
              <textarea
                data-testid={TID.form.description}
                required
                rows={5}
                value={data.description}
                onChange={(e) => update("description", e.target.value)}
                placeholder="Describe your challenge, business requirements, process issues, bottlenecks, delays, inefficiencies, contract concerns, or questions."
                className={inputCls + " resize-y"}
              />
            </div>

            {/* File upload */}
            <div>
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-4">Document Upload (PDF / DOC / XLS / JPG / PNG — max 25 MB each)</div>
              <div
                data-testid={TID.form.dropzone}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
                className={`border-2 border-dashed p-8 text-center cursor-pointer transition-all ${dragOver ? "dropzone-active" : "border-slate-300 hover:border-[#0A2540]"}`}
              >
                <Upload size={28} className="mx-auto text-[#0A2540] mb-3" />
                <p className="text-sm font-medium text-[#0A2540]">Drop files here or click to upload</p>
                <p className="text-xs text-slate-500 mt-1">Contracts · SOPs · Reports · Process Maps · Operational Data</p>
                <input
                  ref={inputRef}
                  data-testid={TID.form.fileInput}
                  type="file"
                  multiple
                  accept={ALLOWED.join(",")}
                  className="hidden"
                  onChange={(e) => addFiles(e.target.files)}
                />
              </div>
              {files.length > 0 && (
                <ul className="mt-3 space-y-2">
                  {files.map((f, i) => (
                    <li key={i} className="flex items-center justify-between text-sm border border-slate-200 px-3 py-2">
                      <span className="flex items-center gap-2 truncate"><FileText size={14} className="text-[#0A2540] shrink-0" /> {f.name}</span>
                      <button type="button" onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500"><X size={14} /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Contact preferences */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-3">Preferred Contact Method</div>
                <div className="flex flex-wrap gap-2">
                  {["Phone Call", "Email", "Virtual Meeting", "In-Person Consultation"].map((m) => (
                    <RadioPill key={m} active={data.preferred_contact_method === m} onClick={() => update("preferred_contact_method", m)}>{m}</RadioPill>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-3">Preferred Contact Time</div>
                <div className="flex flex-wrap gap-2">
                  {["Morning", "Afternoon", "Evening"].map((m) => (
                    <RadioPill key={m} active={data.preferred_contact_time === m} onClick={() => update("preferred_contact_time", m)}>{m}</RadioPill>
                  ))}
                </div>
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                data-testid={TID.form.consent}
                type="checkbox"
                checked={data.consent}
                onChange={(e) => update("consent", e.target.checked)}
                className="mt-1 h-4 w-4 accent-[#10B981]"
              />
              <span className="text-xs text-slate-600 leading-relaxed">
                I consent to Clinivon contacting me regarding my inquiry and understand my information will remain confidential.
              </span>
            </label>

            <button
              data-testid={TID.form.submit}
              disabled={submitting}
              type="submit"
              className="btn-sharp w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-[#0A2540] text-white text-sm font-semibold hover:bg-[#061A30] disabled:opacity-60 transition-all"
            >
              {submitting ? (<><Loader2 size={16} className="animate-spin" /> Submitting…</>) : "Request a Consultation"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

const inputCls = "w-full bg-white border border-slate-300 px-3.5 py-2.5 text-sm text-[#0A2540] focus:outline-none focus:border-[#0A2540] focus:ring-1 focus:ring-[#0A2540] transition-colors";

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-medium text-slate-600 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function RadioPill({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3.5 py-2 text-xs font-medium border transition-colors ${active ? "bg-[#0A2540] text-white border-[#0A2540]" : "bg-white text-slate-700 border-slate-300 hover:border-[#0A2540]"}`}
    >
      {children}
    </button>
  );
}
